import { createWithEqualityFn } from 'zustand/traditional';
import { devtools, persist } from 'zustand/middleware';
import type { ThemeConfig } from 'antd';

interface UIState {
  // Sidebar state
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;

  // Modal states
  isCreateCustomerModalOpen: boolean;
  isBalanceModalOpen: boolean;
  setCreateCustomerModalOpen: (open: boolean) => void;
  setBalanceModalOpen: (open: boolean) => void;

  // Table preferences
  tablePageSize: number;
  setTablePageSize: (size: number) => void;

  // Theme (интегрировано с Ant Design и Tailwind)
  theme: 'light' | 'dark';
  setTheme: (theme: 'light' | 'dark') => void;
  themeTokens: Partial<ThemeConfig['token']> | null;
  themeComponents: Partial<ThemeConfig['components']> | null;
  setThemeTokens: (tokens: Partial<ThemeConfig['token']> | null) => void;
  setThemeComponents: (components: Partial<ThemeConfig['components']> | null) => void;
  resetThemeOverrides: () => void;

  // Search state
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

export const useUIStore = createWithEqualityFn<UIState>()(
  devtools(
    persist(
      (set) => ({
        // Sidebar
        sidebarCollapsed: false,
        setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
        toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

        // Modals
        isCreateCustomerModalOpen: false,
        isBalanceModalOpen: false,
        setCreateCustomerModalOpen: (open) => set({ isCreateCustomerModalOpen: open }),
        setBalanceModalOpen: (open) => set({ isBalanceModalOpen: open }),

        // Table
        tablePageSize: 20,
        setTablePageSize: (size) => set({ tablePageSize: size }),

        // Theme
        theme: 'light',
        setTheme: (theme) => set({ theme }),
        themeTokens: null,
        themeComponents: null,
        setThemeTokens: (tokens) => set({ themeTokens: tokens }),
        setThemeComponents: (components) => set({ themeComponents: components }),
        resetThemeOverrides: () => set({ themeTokens: null, themeComponents: null }),

        // Search
        searchQuery: '',
        setSearchQuery: (query) => set({ searchQuery: query }),
      }),
      {
        name: 'workernet-ui-storage',
        partialize: (state) => ({
          sidebarCollapsed: state.sidebarCollapsed,
          tablePageSize: state.tablePageSize,
          theme: state.theme,
          themeTokens: state.themeTokens,
          themeComponents: state.themeComponents,
        }),
      }
    ),
    { name: 'UIStore' }
  )
);

